import { EntradaDosResumos } from "@/ui/Resumos";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  breakdown, formatIntervalo, formatMoney, formatPoints, formatRating, pointsLabel,
  resolveConfig, type ChallengeConfig, janelaDoPeriodo, janelaAnterior,
  interacaoDe,
} from "@nhomi/core";
import { watchChallenge, type Challenge } from "@/data/challenges";
import { watchMembers, type Member } from "@/data/members";
import { dayKey, periodPlates, toCorePlate, type Plate } from "@/data/plates";
import { Avatar, Empty, Spinner } from "@/ui/primitives";
import { StoragePhoto } from "@/ui/StoragePhoto";
import { IconBack, IconFork } from "@/ui/icons";
import { TabBar } from "@/ui/TabBar";

/* ============================================================
   Recap do período

   Todo número aqui sai dos PRATOS, não da ficha guardada. É de propósito:
   quando os dois divergem, os pratos são a fonte — a ficha é derivada
   deles. No app anterior o recap lia a ficha e mostrava um total que não
   batia com nenhuma lista que dava pra abrir.
   ============================================================ */

const PERIODOS = [
  { id: "week", label: "Semana" },
  { id: "month", label: "Mês" },
  { id: "year", label: "Ano" },
  { id: "all", label: "Tudo" },
] as const;
type Periodo = typeof PERIODOS[number]["id"];

const primeiro = (nome: string) => nome.split(" ")[0] ?? nome;

export function Recap() {
  const { cid = "" } = useParams();
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [periodo, setPeriodo] = useState<Periodo>("week");
  const [plates, setPlates] = useState<Plate[] | null>(null);
  const [antes, setAntes] = useState<Plate[] | null>(null);

  useEffect(() => watchChallenge(cid, setChallenge), [cid]);
  useEffect(() => watchMembers(cid, setMembers), [cid]);

  const janela = useMemo(() => janelaDoPeriodo(periodo, dayKey(), challenge), [periodo, challenge]);
  const janelaAntes = useMemo(() => janelaAnterior(periodo, janela), [periodo, janela]);

  useEffect(() => {
    let vivo = true;
    setPlates(null);
    void periodPlates(cid, janela.de, janela.ate)
      .then((l) => { if (vivo) setPlates(l); })
      .catch(() => { if (vivo) setPlates([]); });
    return () => { vivo = false; };
  }, [cid, janela.de, janela.ate]);

  useEffect(() => {
    let vivo = true;
    setAntes(null);
    if (!janelaAntes) return;
    void periodPlates(cid, janelaAntes.de, janelaAntes.ate)
      .then((l) => { if (vivo) setAntes(l); })
      .catch(() => { if (vivo) setAntes([]); });
    return () => { vivo = false; };
  }, [cid, janelaAntes]);

  const config: ChallengeConfig = useMemo(() => resolveConfig(challenge), [challenge]);

  const resumo = useMemo(() => {
    if (!plates) return null;
    const cozinhados = plates.filter((p) => p.homemade).length;

    // O placar do período sai dos pratos: `dayPoints` é do desafio todo.
    const linhas = members.map((m) => {
      const meus = plates.filter((p) => p.uid === m.uid);
      const b = breakdown(config, meus.map(toCorePlate), { jokerDays: m.jokerDays });
      return { membro: m, b, pratos: meus.length };
    }).sort((a, b) => b.b.total - a.b.total || a.membro.name.localeCompare(b.membro.name, "pt-BR"));

    const comNota = plates.filter((p) => p.ratingCount > 0 && p.ratingAvg != null);
    const melhor = [...comNota].sort((a, b) => (b.ratingAvg ?? 0) - (a.ratingAvg ?? 0))[0];
    const pior = [...comNota].sort((a, b) => (a.ratingAvg ?? 0) - (b.ratingAvg ?? 0))[0];

    const comPreco = plates.filter((p) => (p.price ?? 0) > 0);
    const gasto = comPreco.reduce((s, p) => s + (p.price ?? 0), 0);
    const maisCaro = [...comPreco].sort((a, b) => (b.price ?? 0) - (a.price ?? 0))[0];

    return {
      total: plates.length,
      cozinhados,
      comprados: plates.length - cozinhados,
      linhas,
      melhor, pior,
      gasto,
      semPreco: plates.length - comPreco.length,
      maisCaro,
    };
  }, [plates, members, config]);

  /* Variação contra o período anterior. Sem base pra comparar, some. */
  const delta = (agora: number, anterior: number | null, dinheiro = false) => {
    if (periodo === "all" || anterior === null) return null;
    if (!anterior && !agora) return null;
    if (!anterior) return <span className="delta up">novo</span>;
    const d = agora - anterior;
    if (d === 0) return <span className="delta flat">igual</span>;
    const valor = dinheiro ? formatMoney(Math.abs(d)) : Math.abs(d);
    // Gastar menos não é derrota: dinheiro não ganha cor de bom/ruim.
    const classe = dinheiro ? "flat" : d > 0 ? "up" : "down";
    return <span className={`delta ${classe}`}>{d > 0 ? "+" : "−"}{valor}</span>;
  };

  const cartaoDePrato = (p: Plate | undefined, titulo: string, emoji: string) => p ? (
    <Link className="card highlight-card" to={`/c/${cid}/p/${p.id}`}>
      <div className="hl-photo">
        {p.thumbPath ? <StoragePhoto path={p.thumbPath} alt="" /> : <IconFork size={26} />}
      </div>
      <div className="hl-body">
        <div className="hl-title">{emoji} {titulo}</div>
        <div className="hl-name">{p.title || "Sem título"}</div>
        <div className="hl-sub">
          {p.authorName} · ⭐ {formatRating(p.ratingAvg)} ({p.ratingCount})
        </div>
      </div>
    </Link>
  ) : null;

  const medalhas = ["🥇", "🥈", "🥉"];

  return (
    <main className="screen">
      <div className="topbar">
        <Link className="topbar-btn" to={`/c/${cid}`} aria-label="Voltar"><IconBack /></Link>
        <div className="topbar-title">Recap</div>
        <span className="topbar-btn" />
      </div>

      <div className="screen-body">
        <div className="page-head">
          <h1>{challenge?.name ?? ""}</h1>
          <p className="sub">{formatIntervalo(janela.de, janela.ate, new Date().getFullYear())}</p>
        </div>

        {challenge && <EntradaDosResumos cid={cid} startDay={challenge.startDay ?? ""} />}

        <div className="segmented">
          {PERIODOS.map((p) => (
            <button key={p.id} className={p.id === periodo ? "active" : ""}
              onClick={() => setPeriodo(p.id)}>
              {p.label}
            </button>
          ))}
        </div>

        {!resumo ? <Spinner /> : resumo.total === 0 ? (
          <Empty emoji="📭" title="Nada nesse período">
            <p>Quando a galera postar, o resumo aparece aqui.</p>
          </Empty>
        ) : (
          <>
            <div className="section-label">Pódio</div>
            <div className="card podium">
              {resumo.linhas.slice(0, 3).map((l, i) => (
                <Link key={l.membro.uid} className="podium-item" to={`/c/${cid}/u/${l.membro.uid}`}>
                  <span className="medal">{medalhas[i]}</span>
                  <Avatar name={l.membro.name} photo={l.membro.photo} size="lg" />
                  <span className="who">{primeiro(l.membro.name)}</span>
                  <span className="pts">{formatPoints(l.b.total)} pts</span>
                </Link>
              ))}
            </div>

            <div className="card"><div className="stats-row">
              <div className="stat">
                <div className="v">{resumo.total}</div>
                <div className="k">{resumo.total === 1 ? "prato" : "pratos"}</div>
                {delta(resumo.total, antes?.length ?? null)}
              </div>
              <div className="stat">
                <div className="v">{resumo.cozinhados}</div><div className="k">cozinhados</div>
                {delta(resumo.cozinhados, antes ? antes.filter((p) => p.homemade).length : null)}
              </div>
              <div className="stat">
                <div className="v">{resumo.comprados}</div><div className="k">comprados</div>
              </div>
            </div></div>

            {/* Sem notas no desafio, não existe "prato do período" por nota. */}
            {interacaoDe(challenge) !== "ninguem" && cartaoDePrato(resumo.melhor, "Prato do período", "🏆")}
            {interacaoDe(challenge) !== "ninguem" && resumo.pior && resumo.pior.id !== resumo.melhor?.id
              && cartaoDePrato(resumo.pior, "Rango da Vergonha", "💀")}

            <div className="section-label">De onde vieram os pontos</div>
            <div className="card breakdown">
              {resumo.linhas.map((l, i) => {
                const partes = [
                  l.b.baseHomemade ? `${pointsLabel(l.b.baseHomemade)} de casa` : "",
                  l.b.baseBought ? `${pointsLabel(l.b.baseBought)} comprado` : "",
                  l.b.repeated ? `${l.b.repeated} repetida${l.b.repeated === 1 ? "" : "s"}` : "",
                  l.b.streakBonus ? `+${formatPoints(l.b.streakBonus)} de sequência` : "",
                  l.b.events ? `+${formatPoints(l.b.events)} de evento` : "",
                  l.b.semFoto ? `−${formatPoints(l.b.semFoto)} sem foto` : "",
                ].filter(Boolean).join(" · ");

                return (
                  <Link key={l.membro.uid} className="bd-row" to={`/c/${cid}/u/${l.membro.uid}`}>
                    <span className="bd-label">
                      {i + 1}º {primeiro(l.membro.name)}
                      <div className="bd-conta">{partes || "sem pratos no período"}</div>
                    </span>
                    <span className="bd-valor">{pointsLabel(l.b.total)}</span>
                  </Link>
                );
              })}
              <BarraDePontos linhas={resumo.linhas} />
            </div>

            {resumo.gasto > 0 && (
              <>
                <div className="section-label">Gasto</div>
                <div className="card breakdown">
                  {resumo.maisCaro && (
                    <Link className="bd-row" to={`/c/${cid}/p/${resumo.maisCaro.id}`}>
                      <span className="bd-label">
                        💸 Mais caro do período
                        <div className="bd-conta">
                          {resumo.maisCaro.title || "Sem título"} · {resumo.maisCaro.authorName}
                        </div>
                      </span>
                      <span className="bd-valor">{formatMoney(resumo.maisCaro.price ?? 0)}</span>
                    </Link>
                  )}
                  <div className="bd-row total">
                    <span className="bd-label">
                      Total declarado
                      <div className="bd-conta">
                        média {formatMoney(resumo.gasto / Math.max(1, resumo.total - resumo.semPreco))} por prato
                        {resumo.semPreco > 0 && ` · ${resumo.semPreco} sem preço`}
                      </div>
                    </span>
                    <span className="bd-valor">{formatMoney(resumo.gasto)}</span>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        <div className="gap" />
      </div>

      <TabBar cid={cid} />
    </main>
  );
}

/** A barra que mostra o peso de cada parte no total do grupo. */
function BarraDePontos(
  { linhas }: { linhas: { b: ReturnType<typeof breakdown> }[] },
) {
  /* Os valores vêm do detalhamento, não de "cozinhados × 2": com peso por
     refeição, multiplicar pelo valor de tabela mente sempre que alguém
     posta uma refeição que vale diferente. */
  const soma = linhas.reduce((acc, l) => ({
    casa: acc.casa + l.b.baseHomemade,
    comprado: acc.comprado + l.b.baseBought,
    bonus: acc.bonus + l.b.streakBonus,
    extra: acc.extra + l.b.guessed + l.b.events,
  }), { casa: 0, comprado: 0, bonus: 0, extra: 0 });

  const total = soma.casa + soma.comprado + soma.bonus + soma.extra;
  if (!total) return null;
  const pct = (v: number) => `${(v / total) * 100}%`;

  return (
    <>
      <div className="bd-bar">
        {soma.casa > 0 && <span className="casa" style={{ width: pct(soma.casa) }} />}
        {soma.comprado > 0 && <span className="comprado" style={{ width: pct(soma.comprado) }} />}
        {soma.bonus > 0 && <span className="bonus" style={{ width: pct(soma.bonus) }} />}
        {soma.extra > 0 && <span className="extra" style={{ width: pct(soma.extra) }} />}
      </div>
      <div className="bd-legenda">
        {soma.casa > 0 && <span><i className="casa" />Cozinhado {pointsLabel(soma.casa)}</span>}
        {soma.comprado > 0 && <span><i className="comprado" />Comprado {pointsLabel(soma.comprado)}</span>}
        {soma.bonus > 0 && <span><i className="bonus" />Sequência {pointsLabel(soma.bonus)}</span>}
        {soma.extra > 0 && <span><i className="extra" />Cravadas e eventos {pointsLabel(soma.extra)}</span>}
      </div>
    </>
  );
}
